import { useEffect, useRef } from 'react';
import { useForm, UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Platform, ScrollView, StyleSheet, View } from 'react-native';
import { Layout } from '../../styles';
import { Colors } from '../../themes';
import { Button } from '../crop-circle/Button';
import { FormContext } from '../forms';
import { PopUpView } from '../modals/popUpView';
import { BottomComponentBar } from '../navigation/bottomComponentBar';
import type { BottomComponentBarBaseProps } from '../navigation/types';
import type { FilterContentProps } from './filterContent';
import { FilterContent } from './filterContent';
import { filterScreenStyles } from './filterScreenStyles';

export type FilterScreenLayoutProps = FilterContentProps & {
  mode: 'page' | 'modal';
  onBack?: () => void;
  onDismiss?: () => void;
  isVisible?: boolean;
  formMethods: ReturnType<typeof useForm>;
  resultsCount: number;
  viewResultsTranslationKey: string;
  onReset: () => void | Promise<void>;
  canReset: boolean | (() => boolean);
  onSubmit: (data: any) => void | Promise<void>;
  bottomBar?: React.ComponentType<BottomComponentBarBaseProps>;
};

export function FilterScreenLayout({
  mode,
  onDismiss,
  isVisible,
  formMethods,
  resultsCount,
  viewResultsTranslationKey,
  onReset,
  canReset,
  onSubmit,
  variant,
  sections,
  control,
  setValue,
  selectedDateType,
  workspaceId,
  isGlobalAdmin,
  isOEM,
  bottomBar,
}: FilterScreenLayoutProps) {
  const { t } = useTranslation();
  const BottomBar = bottomBar ?? BottomComponentBar;
  const { handleSubmit, watch } = formMethods;
  const onSubmitRef = useRef(onSubmit);
  onSubmitRef.current = onSubmit;
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  // Debounce user clicks before handleSubmit is called
  useEffect(() => {
    if (Platform.OS === 'web') {
      const subscription = watch(() => {
        clearTimeout(debounceTimerRef.current);

        debounceTimerRef.current = setTimeout(() => {
          handleSubmit(onSubmitRef.current)();
        }, 1000);
      });

      return () => {
        subscription.unsubscribe();
        clearTimeout(debounceTimerRef.current);
      };
    }

    return;
  }, [watch, handleSubmit]);

  const resetEnabled =
    typeof canReset === 'function' ? canReset() : canReset ?? false;
  const renderMode = mode ?? (Platform.OS === 'web' ? 'modal' : 'page');

  const ViewResultsButton = () => (
    <Button
      style={
        [
          filterScreenStyles.viewResultsButton,
          !resetEnabled && filterScreenStyles.viewResultsButtonWithMargin,
        ] as any
      }
      onPress={handleSubmit(onSubmit)}
      state="active"
    >
      {t(viewResultsTranslationKey, { count: resultsCount })}
    </Button>
  );

  const footer = (
    <BottomBar
      style={{
        paddingLeft: 0,
        paddingRight: 8,
      }}
      backButton={<View />}
      additionalHeaderBlock={<ViewResultsButton />}
      onRightPress={resetEnabled ? onReset : undefined}
    />
  );

  const scrollContentStyle = [
    styles.scrollContent,
    renderMode === 'modal'
      ? filterScreenStyles.scrollContentModal
      : filterScreenStyles.scrollContentPage,
    renderMode === 'modal' && styles.modalScrollContent,
  ];

  const filterPage = (
    <FormContext.Provider value={formMethods as UseFormReturn}>
      <View style={styles.container}>
        <ScrollView
          style={styles.scrollView}
          contentContainerStyle={scrollContentStyle}
        >
          <FilterContent
            variant={variant}
            sections={sections}
            control={control}
            setValue={setValue}
            selectedDateType={selectedDateType}
            workspaceId={workspaceId}
            isGlobalAdmin={isGlobalAdmin}
            isOEM={isOEM}
          />
        </ScrollView>
        {footer}
      </View>
    </FormContext.Provider>
  );

  if (renderMode === 'modal') {
    return (
      <PopUpView
        isVisible={isVisible ?? false}
        onDismiss={onDismiss ?? (() => {})}
        containerStyle={{
          maxWidth: 600,
          maxHeight: 700,
          width: '90%',
          height: '90%',
        }}
      >
        {filterPage}
      </PopUpView>
    );
  }

  return filterPage;
}

const styles = StyleSheet.create({
  container: {
    ...Layout.container,
    backgroundColor: Colors.brand.white,
    flex: 1,
    position: 'relative',
  },
  header: {
    paddingHorizontal: 16,
    paddingTop: 16,
    paddingBottom: 8,
  },
  headerTitleText: {
    fontSize: 24,
    fontWeight: '600',
    color: Colors.black,
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    paddingHorizontal: 16,
    paddingBottom: 100,
  },
  modalScrollContent: {
    paddingBottom: 100,
  },
});
